import type { LoyaltyReview } from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  Dialog,
  EmptyState,
  FormField,
  MoneyText,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ScanEye } from "lucide-react";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatPoints, ownerText, reviewOwner } from "../../../features/loyalty/loyalty-values.js";
import {
  useLoyaltyPermissions,
  useLoyaltyReviews,
  useRefreshLoyalty,
} from "../../../features/loyalty/queries.js";
import { LoyaltyPage, OwnerCell } from "../../../features/loyalty/shared.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDateTime } from "../../../lib/format.js";

export const Route = createFileRoute("/_app/sadakat/inceleme")({ component: ReviewsRoute });

/**
 * Kötüye kullanım inceleme kuyruğu (ADR 0017 §22): pencere içinde çok sık kazanım ya da günlük tavanı aşan kazanım
 * `PENDING_REVIEW` olur (bakiyeye girmez). Onay partiyi açar ve bakiyeye ekler; ret gerekçelidir (denetim izine yazılır).
 * Kişisel veri yok: müşteri koduyla gösterilir, ayrıntı müşteri kartındadır (maskeleme kuralları orada).
 */
function ReviewsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <LoyaltyPage title={t("menu.loyaltyReviews")} description={t("loyalty.reviews.description")}>
      <ReviewsBody />
    </LoyaltyPage>
  );
}

function ReviewsBody(): ReactElement {
  const { t } = useTranslation();
  const permissions = useLoyaltyPermissions();
  const pages = useCursorPages("reviews");
  const list = useLoyaltyReviews(pages.cursor);
  const [deciding, setDeciding] = useState<{ review: LoyaltyReview; approve: boolean } | null>(null);

  const columns = useMemo<DataTableColumn<LoyaltyReview>[]>(
    () => [
      {
        id: "occurredAt",
        header: t("loyalty.reviews.occurredAt"),
        hideable: false,
        exportValue: (row) => row.occurredAt,
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDateTime(row.occurredAt)}</span>,
      },
      {
        id: "customer",
        header: t("loyalty.reviews.customer"),
        exportValue: (row) => ownerText(reviewOwner(row)),
        cell: (row) => <OwnerCell owner={reviewOwner(row)} />,
      },
      {
        id: "station",
        header: t("loyalty.fields.station"),
        exportValue: (row) => row.stationName,
        cell: (row) => row.stationName ?? <span className="text-muted-foreground">—</span>,
      },
      {
        id: "sale",
        header: t("loyalty.reviews.sale"),
        exportValue: (row) => row.sourceDocId,
        cell: (row) =>
          row.sourceDocType === "POS_SALE" ? (
            <Link
              to="/kasa/satislar/$id"
              params={{ id: row.sourceDocId }}
              className="relative whitespace-nowrap underline underline-offset-4"
            >
              {t("loyalty.reviews.openSale")}
              <span className="sr-only"> — {row.customerCode}</span>
            </Link>
          ) : (
            <span className="font-mono text-xs">{row.sourceDocType}</span>
          ),
      },
      {
        id: "points",
        header: t("loyalty.fields.points"),
        align: "right",
        exportValue: (row) => row.points,
        cell: (row) => <span className="tabular whitespace-nowrap">{formatPoints(row.points)}</span>,
      },
      {
        id: "value",
        header: t("loyalty.fields.value"),
        align: "right",
        exportValue: (row) => row.value,
        cell: (row) => <MoneyText value={row.value} />,
      },
      {
        id: "verdict",
        header: t("loyalty.reviews.verdict"),
        exportValue: (row) => row.verdict,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>
              {row.verdict === "TOO_FREQUENT" || row.verdict === "DAILY_CAP"
                ? t(`loyalty.reviews.verdicts.${row.verdict}`)
                : (row.verdict ?? t("loyalty.reviews.verdicts.unknown"))}
            </span>
            {row.reason ? <span className="text-xs text-muted-foreground">{row.reason}</span> : null}
          </span>
        ),
      },
      {
        id: "actions",
        header: t("common.actions"),
        cell: (row) =>
          permissions.canManage ? (
            <span className="flex flex-wrap gap-2">
              <Button
                size="sm"
                className="relative"
                onClick={() => setDeciding({ review: row, approve: true })}
              >
                {t("loyalty.reviews.approve")}
                <span className="sr-only"> — {row.customerCode}</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="relative"
                onClick={() => setDeciding({ review: row, approve: false })}
              >
                {t("loyalty.reviews.reject")}
                <span className="sr-only"> — {row.customerCode}</span>
              </Button>
            </span>
          ) : null,
      },
    ],
    [t, permissions.canManage],
  );

  return (
    <>
      <DataTable
        caption={t("loyalty.reviews.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<ScanEye />}
            title={t("loyalty.reviews.emptyTitle")}
            description={t("loyalty.reviews.emptyDescription")}
          />
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
      />
      <ReviewDecisionDialog target={deciding} onClose={() => setDeciding(null)} />
    </>
  );
}

function ReviewDecisionDialog({
  target,
  onClose,
}: {
  target: { review: LoyaltyReview; approve: boolean } | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshLoyalty();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (target) {
      setReason("");
      setError(null);
    }
  }, [target]);

  const decide = useMutation({
    mutationFn: async () => {
      const { review, approve } = target!;
      if (approve)
        return (
          await api.POST("/loyalty/reviews/{entryId}/approve", { params: { path: { entryId: review.id } } })
        ).data!;
      return (
        await api.POST("/loyalty/reviews/{entryId}/reject", {
          params: { path: { entryId: review.id } },
          body: { reason: reason.trim() },
        })
      ).data!;
    },
    onSuccess: async () => {
      await refresh();
      toast({
        tone: "success",
        title: target?.approve ? t("loyalty.reviews.approved") : t("loyalty.reviews.rejected"),
      });
      onClose();
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  const approve = target?.approve ?? true;
  return (
    <Dialog
      open={target !== null}
      onOpenChange={(next) => {
        if (!next && !decide.isPending) onClose();
      }}
      title={approve ? t("loyalty.reviews.approveTitle") : t("loyalty.reviews.rejectTitle")}
      description={
        target
          ? t("loyalty.reviews.dialogDescription", {
              code: ownerText(reviewOwner(target.review)),
              points: formatPoints(target.review.points),
            })
          : undefined
      }
      dismissible={!decide.isPending}
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          decide.mutate();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        {approve ? (
          <p className="text-sm text-muted-foreground">{t("loyalty.reviews.approveHint")}</p>
        ) : (
          <FormField
            label={t("loyalty.fields.reason")}
            description={t("loyalty.reviews.rejectHint")}
            required
          >
            {(control) => (
              <Textarea
                {...control}
                rows={2}
                maxLength={300}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </FormField>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onClose} disabled={decide.isPending}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            variant={approve ? "primary" : "destructive"}
            loading={decide.isPending}
            disabled={!approve && reason.trim().length < 3}
          >
            {approve ? t("loyalty.reviews.approve") : t("loyalty.reviews.reject")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
